export const TRIAL_DURATION_DAYS = 30;
export const TRIAL_DURATION_MS = TRIAL_DURATION_DAYS * 24 * 60 * 60 * 1000;
export const TRIAL_VERSION = "30_day_all_inclusive_v1";

export const buildTrialFields = (nowMs = Date.now(), source = "signup") => ({
  trialStatus: "active",
  trialStartedAt: new Date(nowMs).toISOString(),
  trialEndsAt: new Date(nowMs + TRIAL_DURATION_MS).toISOString(),
  trialConvertedAt: null,
  trialSource: source,
  trialVersion: TRIAL_VERSION,
  trialStartDate: new Date(nowMs).toISOString(),
  trialEndDate: new Date(nowMs + TRIAL_DURATION_MS).toISOString(),
  subscriptionStatus: "trial",
  planTier: "pro",
  productTier: "pro",
  trialProductTier: "pro",
});

export const getTrialStatus = (trialStartDate, trialEndDate, explicitStatus = "") => {
  const toDate = (value) => {
    if (!value) return null;

    if (typeof value?.toDate === "function") {
      return value.toDate();
    }

    if (typeof value?.toMillis === "function") {
      return new Date(value.toMillis());
    }

    if (value instanceof Date) {
      return value;
    }

    if (
      typeof value === "object" &&
      typeof value.seconds === "number"
    ) {
      return new Date(value.seconds * 1000);
    }

    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const startDate = toDate(trialStartDate);
  const endDate = toDate(trialEndDate);
  const normalizedExplicitStatus = String(explicitStatus || "").toLowerCase();

  if (["converted", "ineligible"].includes(normalizedExplicitStatus)) {
    return {
      isTrialActive: false,
      isTrialExpired: normalizedExplicitStatus !== "converted",
      daysRemaining: 0,
      hoursRemaining: 0,
      minutesRemaining: 0,
      secondsRemaining: 0,
      totalMinutesRemaining: 0,
      totalSecondsRemaining: 0,
      timeRemainingMs: 0,
      formattedTimeRemaining: normalizedExplicitStatus === "converted" ? "Converted" : "Expired",
      shortFormattedTimeRemaining: "00:00:00",
      status: normalizedExplicitStatus,
    };
  }

  if (!startDate || !endDate) {
    return {
      isTrialActive: false,
      isTrialExpired: true,
      daysRemaining: 0,
      hoursRemaining: 0,
      minutesRemaining: 0,
      secondsRemaining: 0,
      totalMinutesRemaining: 0,
      totalSecondsRemaining: 0,
      totalHoursRemaining: 0,
      timeRemainingMs: 0,
      formattedTimeRemaining: "Expired",
      shortFormattedTimeRemaining: "00:00:00",
      status: "expired",
    };
  }

  const now = new Date();
  const timeRemainingMs = endDate.getTime() - now.getTime();
  const isTrialActive = timeRemainingMs > 0;
  const isTrialExpired = !isTrialActive;

  const safeRemainingMs = Math.max(0, timeRemainingMs);

  const totalSecondsRemaining = Math.floor(safeRemainingMs / 1000);
  const totalMinutesRemaining = Math.floor(safeRemainingMs / (1000 * 60));
  const totalHoursRemaining = Math.floor(safeRemainingMs / (1000 * 60 * 60));
  const daysRemaining = Math.floor(safeRemainingMs / (1000 * 60 * 60 * 24));
  const hoursRemaining = Math.floor(
    (safeRemainingMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)
  );
  const minutesRemaining = Math.floor(
    (safeRemainingMs % (1000 * 60 * 60)) / (1000 * 60)
  );
  const secondsRemaining = Math.floor(
    (safeRemainingMs % (1000 * 60)) / 1000
  );

  const shortFormattedTimeRemaining = [
    String(daysRemaining * 24 + hoursRemaining).padStart(2, "0"),
    String(minutesRemaining).padStart(2, "0"),
    String(secondsRemaining).padStart(2, "0"),
  ].join(":");

  let formattedTimeRemaining = shortFormattedTimeRemaining;

  if (safeRemainingMs >= 1000 * 60 * 60 * 24) {
    if (daysRemaining === 1) {
      formattedTimeRemaining = "1 day";
    } else {
      formattedTimeRemaining = `${daysRemaining} days`;
    }
  } else if (safeRemainingMs >= 1000 * 60 * 60) {
    formattedTimeRemaining = [
      String(hoursRemaining).padStart(2, "0"),
      String(minutesRemaining).padStart(2, "0"),
      String(secondsRemaining).padStart(2, "0"),
    ].join(":");
  } else {
    formattedTimeRemaining = [
      String(minutesRemaining).padStart(2, "0"),
      String(secondsRemaining).padStart(2, "0"),
    ].join(":");
  }

  let status = normalizedExplicitStatus === "ending_soon" ? "warning" : "active";

  if (isTrialExpired) {
    status = "expired";
  } else if (daysRemaining <= 3 || totalHoursRemaining <= 72) {
    status = "warning";
  }

  return {
    isTrialActive,
    isTrialExpired,
    daysRemaining,
    hoursRemaining,
    minutesRemaining,
    secondsRemaining,
    totalMinutesRemaining,
    totalSecondsRemaining,
    totalHoursRemaining,
    timeRemainingMs: safeRemainingMs,
    formattedTimeRemaining,
    shortFormattedTimeRemaining,
    status,
  };
};
